import { Settlement } from '../../../models/Entities/Settlements/Settlement';
import { Sponsor } from '../../../models/Entities/Sponsor/Sponsor';
import { Practitioner } from '../../../models/Entities/Practitioners/Practitioner';
import { MedicalInsurance } from '../../../models/Entities/MedicalInsurances/MedicalInsurance';
import { providerOf, resolveSettlementKind } from '../../../functions/SettlementKind.Functions';

/** Contraparte de cada liquidación de la lista (spec 010 suma el proveedor), sin React ni store. */

export type SettlementListEntityType = 'sponsor' | 'practitioner' | 'agreement' | 'provider';

export interface CounterpartLists {
    sponsorList?: Sponsor[];
    practitionerList?: Practitioner[];
    medicalInsuranceList?: MedicalInsurance[];
}

export interface SettlementListEntity {
    label?: string;
    type?: SettlementListEntityType;
}

/**
 * Nombre y tipo de la contraparte, con el mismo orden que la cabecera (`resolveSettlementKind`): el proveedor
 * (tipo 4) usa la razón social de la asociación; si falta, `providerFallback` (texto ya traducido).
 */
export const settlementEntityOf = (item: Settlement, lists: CounterpartLists, providerFallback: string): SettlementListEntity => {
    const kind = resolveSettlementKind(item);
    if (kind === 'provider') {
        const businessName = providerOf(item)?.businessName?.trim();
        return { label: businessName || providerFallback, type: 'provider' };
    }
    if (kind === 'sponsor') {
        const sponsor = lists.sponsorList?.find((candidate) => candidate.id === item.sponsor[0].sponsorId);
        const name = sponsor ? (sponsor.person?.businessName || [sponsor.person?.firstName, sponsor.person?.lastName].filter(Boolean).join(' ')) : undefined;
        return { label: name ?? 'Sponsor', type: 'sponsor' };
    }
    if (kind === 'practitioner') {
        const practitioner = lists.practitionerList?.find((candidate) => candidate.userId === item.practitionerId);
        return { label: practitioner?.fullName ?? 'Médico', type: 'practitioner' };
    }
    if (kind === 'agreement') {
        const insurance = lists.medicalInsuranceList?.find((candidate) => candidate.id === item.medicalInsuranceId);
        return { label: insurance?.businessName ?? insurance?.title ?? 'Obra social', type: 'agreement' };
    }
    return {};
};

export interface MissingCounterparts {
    sponsors: number[];
    practitioners: string[];
    insurances: number[];
}

/** Contrapartes que faltan en el store y hay que pedir; el proveedor no pide nada (trae su razón social). */
export const missingCounterparts = (items: Settlement[], lists: CounterpartLists): MissingCounterparts => {
    const sponsors = new Set<number>();
    const practitioners = new Set<string>();
    const insurances = new Set<number>();
    items.forEach((item) => {
        const kind = resolveSettlementKind(item);
        if (kind === 'sponsor') {
            const id = item.sponsor[0].sponsorId;
            if (!lists.sponsorList?.find((sponsor) => sponsor.id === id)) sponsors.add(id);
        } else if (kind === 'practitioner') {
            const id = item.practitionerId;
            if (id && !lists.practitionerList?.find((practitioner) => practitioner.userId === id)) practitioners.add(id);
        } else if (kind === 'agreement') {
            const id = item.medicalInsuranceId;
            if (id && !lists.medicalInsuranceList?.find((insurance) => insurance.id === id)) insurances.add(id);
        }
    });
    return { sponsors: Array.from(sponsors), practitioners: Array.from(practitioners), insurances: Array.from(insurances) };
};
